import { FileHelper } from '@helloself/hello-self-ui';
import { createSelector } from '@reduxjs/toolkit';
import { getUnixTime } from 'date-fns';
import { memoize, orderBy } from 'lodash';

import { ConnectionsProfile } from '../../../api/expert/types';
import { ConversationStatus } from '../../../api/messaging/types';
import { RootState } from '../../../app/store';
import { selectMyCognitoId } from '../../user/selectors';
//import { selectAllExpertProfiles } from '../../connections/model/selectors';
//import { ConnectionsProfile } from '../../connections/model/types';
import { getFullName, getOtherParticipant } from '../helpers';
import { ConversationListData, ConversationMessageWithPending, ParticipantDetails } from './types';

export const selectAllExpertProfiles = (state: RootState) => state.connections.profiles;

export const selectLatestSyncTimestamp = (state: RootState) => state.messaging.conversations.forEventsUntilTimestamp;

export const selectHaveUnreadMessages = (state: RootState) => state.messaging.haveUnreadMessages;
export const selectExpertConversationMessages = (state: RootState) => state.messaging.messages;
export const selectPendingMessages = (state: RootState) => state.messaging.pendingMessages;
export const selectFailedMessages = (state: RootState) => state.messaging.failedToSendMessages;
export const selectExpertConversations = (state: RootState) => state.messaging.conversations;
export const selectActiveExpertConversation = (state: RootState) => state.messaging.activeConversationId;
export const selectMostRecentMessagePerConversation = (state: RootState) => state.messaging.mostRecentMessages;
export const selectHasInitialisedMessages = (state: RootState) => state.messaging.hasInitialisedMessages;
export const selectIsLoadingConversations = (state: RootState) => state.messaging.isLoadingConversations;
export const selectIsInsertingNewConversation = (state: RootState) => state.messaging.isInsertingNewConversation;
export const selectDidErrorWhileLoadingConversations = (state: RootState) =>
    state.messaging.didErrorWhileLoadingConversations;
export const selectDidErrorWhileLoadingMessages = (state: RootState) => state.messaging.didErrorWhileLoadingMessages;

export const selectExpertConversationById = createSelector(selectExpertConversations, (conversations) =>
    memoize((conversationId: string) =>
        conversations.conversationStatuses.find(
            (conversation: ConversationStatus) => conversation.conversationId === conversationId
        )
    )
);

export const selectHasInitialisedMessagesForConversationId = createSelector(
    selectHasInitialisedMessages,
    (convosWithMessagesPartLoaded) =>
        memoize((conversationId: string) => convosWithMessagesPartLoaded.some((id) => id === conversationId))
);

export const selectDidErrorWhileLoadingMessagesById = createSelector(
    selectDidErrorWhileLoadingMessages,
    (conversationsThatFailed) =>
        memoize((conversationId: string) => conversationsThatFailed.some((id) => id === conversationId))
);

export const selectExpertConversationLatestSyncedMessageSeqId = createSelector(
    [selectExpertConversationMessages],
    (allMessages) => memoize((conversationId: string) => allMessages[conversationId].latestMessageSeqId)
);

export const selectExpertConversationMessagesById = createSelector(
    [selectExpertConversationMessages, selectPendingMessages, selectFailedMessages],
    (allMessages, pendingMessages, failedMessages) =>
        memoize((conversationId: string) => {
            const haveKey = Object.keys(allMessages).includes(conversationId);
            if (haveKey) {
                const sentMessages: ConversationMessageWithPending[] = allMessages[conversationId].messages;
                let sendingMessages: ConversationMessageWithPending[] = [];
                let erroredMessages: ConversationMessageWithPending[] = [];

                if (pendingMessages && pendingMessages[conversationId]) {
                    sendingMessages = pendingMessages[conversationId].map(
                        (msg) =>
                            ({
                                messageId: '',
                                tempId: msg.tempId,
                                seqId: msg.seqId,
                                messageBody: msg.messageBody,
                                replyToMessageId: msg.replyToMessageId,
                                attachments: msg.attachments,
                                createdAt: msg.createdAt,
                                createdBy: msg.createdBy,
                                sendingStatus: 'pending',
                            } as ConversationMessageWithPending)
                    );

                    sendingMessages = sendingMessages.filter((pendingMsg) => {
                        const matchingSentMessage = sentMessages.some((sentMsg) => {
                            const sameSeqId = sentMsg.seqId === pendingMsg.seqId;
                            const sameAuthor = sentMsg.createdBy.userId === pendingMsg.createdBy.userId;
                            return sameSeqId && sameAuthor;
                        });

                        return !matchingSentMessage;
                    });
                }

                if (failedMessages && failedMessages[conversationId]) {
                    erroredMessages = failedMessages[conversationId].map((msg) => ({
                        messageId: '',
                        tempId: msg.tempId,
                        seqId: msg.seqId,
                        messageBody: msg.messageBody,
                        replyToMessageId: msg.replyToMessageId,
                        attachments: msg.attachments,
                        createdAt: msg.createdAt,
                        createdBy: msg.createdBy,
                        sendingStatus: 'failed',
                    }));
                }

                const zippedMessages = orderBy(
                    [...sentMessages, ...sendingMessages, ...erroredMessages],
                    'seqId',
                    'asc'
                );
                return zippedMessages;
            } else {
                return [];
            }
        })
);

export const selectExpertConversationByPossiblyNullId = createSelector(selectExpertConversations, (conversations) =>
    memoize((conversationId: string | undefined | null) =>
        conversationId
            ? conversations.conversationStatuses.find(
                  (conversation: ConversationStatus) => conversation.conversationId === conversationId
              )
            : undefined
    )
);

export const selectExpertConversationByOtherParticipantId = createSelector(selectExpertConversations, (conversations) =>
    memoize((expertId: string) => {
        const conversation = conversations.conversationStatuses.find(
            (convo: ConversationStatus) =>
                convo.participants.length === 2 && convo.participants.some((person) => person.userId === expertId)
        );
        return conversation;
    })
);

export const selectConversationListData = createSelector(
    [selectExpertConversations, selectMyCognitoId, selectAllExpertProfiles, selectExpertConversationMessages],
    (conversations, expertId, expertProfiles, messagesMap) => {
        const listData: ConversationListData[] = conversations.conversationStatuses.map((convo: ConversationStatus) => {
            const otherParticipantId = getOtherParticipant(convo, expertId) || '';
            let otherParticipant: ParticipantDetails = {
                id: '',
                firstName: '(no',
                lastName: 'name)',
                fullName: '(no name)',
                jobTitle: '',
            };
            if (otherParticipantId) {
                const profile = expertProfiles.find((prof: ConnectionsProfile) => prof.expertId === otherParticipantId);
                if (profile) {
                    otherParticipant = {
                        id: otherParticipantId,
                        title: profile.title,
                        firstName: profile.firstName,
                        lastName: profile.lastName,
                        fullName: getFullName(profile.firstName, profile.lastName, profile.title),
                        jobTitle: profile.jobTitle,
                    };
                }
            }

            let mostRecentMessageText: string | undefined = 'No message data';
            const messageStatus = messagesMap[convo.conversationId];
            if (messageStatus) {
                const mostRecentMessage = messageStatus.messages[messageStatus.messages.length - 1];
                if (mostRecentMessage && mostRecentMessage.attachments?.length > 0) {
                    const attachment = mostRecentMessage.attachments[0];
                    if (attachment && mostRecentMessage?.messageBody === '') {
                        if (FileHelper.isImageFilename(attachment.fileDisplayName)) {
                            mostRecentMessageText = 'Photo';
                        } else if (FileHelper.isVideoFilename(attachment.fileDisplayName)) {
                            mostRecentMessageText = 'Video';
                        } else if (FileHelper.isDocumentFilename(attachment.fileDisplayName)) {
                            mostRecentMessageText = 'Document';
                        }
                    } else {
                        mostRecentMessageText = mostRecentMessage.messageBody;
                    }
                }
            }

            return {
                conversationId: convo.conversationId,
                otherParticipantId: otherParticipantId,
                otherParticipant: otherParticipant,
                latestMessageText: mostRecentMessageText,
                latestMessageTimestampUTC: getUnixTime(new Date(convo.latestMessageTimestamp)),
                latestMessageSeqId: convo.latestMessageSeqId,
                unreadMessageCount: convo.latestMessageSeqId - convo.latestReadSeqId,
            };
        });

        return orderBy(listData, 'latestMessageTimestampUTC', 'desc');
    }
);

export const selectMostRecentMessageByConversationId = createSelector(
    [selectExpertConversationMessages, selectPendingMessages],
    (allMessages, pendingMessages) =>
        memoize((conversationId: string) => {
            const sentMessages = allMessages[conversationId];
            const sendingMessages = pendingMessages[conversationId];

            let mostRecentSent = undefined;
            let mostRecentPending = undefined;

            if (sentMessages && sentMessages.messages.length > 0) {
                mostRecentSent = sentMessages.messages[sentMessages.messages.length - 1];
            }

            if (sendingMessages && sendingMessages.length > 0) {
                mostRecentPending = sendingMessages[sendingMessages.length - 1];
            }

            if (mostRecentPending && mostRecentPending.seqId > (mostRecentSent?.seqId || 0)) {
                return mostRecentPending;
            } else if (mostRecentSent) {
                return mostRecentSent;
            } else {
                return undefined;
            }
        })
);

export const selectUnreadMessagesForExpertConversations = createSelector(selectExpertConversations, (conversations) =>
    memoize((conversationId: string | undefined) =>
        conversations.conversationStatuses
            .map((convo: ConversationStatus) => {
                // Only return a count of conversations we're not currently looking at
                if (conversationId /* && conversationId !== convo.conversationId*/) {
                    return convo.latestMessageSeqId - convo.latestReadSeqId;
                } else {
                    return 0;
                }
            })
            .reduce((previousValue: number, currentValue: number) => (previousValue || 0) + (currentValue || 0), 0)
    )
);

export const selectUnreadMessagesForCurrentExpertConversation = createSelector(
    selectExpertConversations,
    (conversations) =>
        memoize((conversationId: string | undefined) => {
            const status = conversations.conversationStatuses.find(
                (convoStatus: ConversationStatus) => convoStatus.conversationId === conversationId
            );
            if (status) {
                const unreadCount = status.latestMessageSeqId - status.latestReadSeqId;
                return unreadCount;
            } else {
                return 0;
            }
        })
);

export const selectConversationsBySearchTerm = createSelector([selectConversationListData], (conversations) =>
    memoize((searchTerm: string) =>
        conversations.filter((listData) =>
            listData.otherParticipant.fullName.toLowerCase().includes(searchTerm.toLowerCase())
        )
    )
);

export const selectExpertConversationParticipantsByIds = createSelector(
    [selectAllExpertProfiles, selectMyCognitoId],
    (profiles, myCognitoId) =>
        memoize((conversation: ConversationStatus | null | undefined): ConnectionsProfile[] => {
            const myProfile = (profiles as ConnectionsProfile[]).find((profile) => profile.expertId === myCognitoId);
            if (!conversation) {
                return [];
            }

            const participantIds = conversation.participants.map((participant) => participant.userId);
            const participantProfiles = profiles.filter((profile) => participantIds.includes(profile.expertId));
            if (myProfile) {
                participantProfiles.push(myProfile);
            }
            return participantProfiles;
        })
);

export const selectUnreadMessagesCount = createSelector([selectExpertConversations], (conversations) => {
    let totalUnreadMessages = 0;

    for (const conversation of conversations.conversationStatuses) {
        const unreadMessages = conversation.latestMessageSeqId - conversation.latestReadSeqId;
        if (unreadMessages > 0) {
            totalUnreadMessages += unreadMessages;
        }
    }

    return totalUnreadMessages;
});
